import api from './api';

const therapistApi = {
  // Audio Export
  requestAudioExport: async (conversationId, config) => {
    const token = localStorage.getItem('token');
    return await api.post(`/therapist/conversation/${conversationId}/export-audio`, config, token);
  },

  getAudioExports: async (params = {}) => {
    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    return await api.get(`/therapist/audio-exports${queryString ? `?${queryString}` : ''}`, token);
  },

  getAudioExportDetails: async (jobId) => {
    const token = localStorage.getItem('token');
    return await api.get(`/therapist/audio-exports/${jobId}`, token);
  },

  deleteAudioExport: async (jobId) => {
    const token = localStorage.getItem('token');
    return await api.delete(`/therapist/audio-exports/${jobId}`, token);
  },

  retryAudioExport: async (jobId) => {
    const token = localStorage.getItem('token');
    return await api.post(`/therapist/audio-exports/${jobId}/retry`, {}, token);
  },

  getAudioExportDownloadUrl: async (jobId) => {
    const token = localStorage.getItem('token');
    return await api.get(`/therapist/audio-exports/${jobId}/download`, token);
  },

  // Report Generation
  checkReportPrerequisites: async (clientId) => {
    const token = localStorage.getItem('token');
    const response = await api.get(`/therapist/client/${clientId}/report/prerequisites`, token);
    return response.data || response; // Handle different response structures
  },

  generateReport: async (clientId, config) => {
    const token = localStorage.getItem('token');
    const response = await api.post(`/therapist/client/${clientId}/report/generate`, config, token);
    
    // Handle different response types based on format
    if (config.format === 'pdf' || config.format === 'markdown') {
      // For binary data, we need to set responseType
      const binaryResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/therapist/client/${clientId}/report/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(config)
      });
      
      if (!binaryResponse.ok) {
        const error = await binaryResponse.json();
        throw new Error(error.message || 'Failed to generate report');
      }
      
      return {
        data: await binaryResponse.blob()
      };
    }
    
    return response;
  },

  generateMissingSummaries: async (clientId) => {
    const token = localStorage.getItem('token');
    const response = await api.post(`/therapist/client/${clientId}/report/generate-summaries`, {}, token);
    return response.data || response;
  },

  // Existing therapist API methods from api.js
  getAssignedClients: async () => {
    const token = localStorage.getItem('token');
    return await api.getAssignedClients(token);
  },

  getClientConversations: async (clientId) => {
    const token = localStorage.getItem('token');
    return await api.getClientConversations(clientId, token);
  },

  getClientCheckIns: async (clientId) => {
    const token = localStorage.getItem('token');
    return await api.getClientCheckIns(clientId, token);
  },

  getTherapistClientGoals: async (clientId) => {
    const token = localStorage.getItem('token');
    return await api.getTherapistClientGoals(clientId, token);
  },

  getTherapistClientJournal: async (clientId) => {
    const token = localStorage.getItem('token');
    return await api.getTherapistClientJournal(clientId, token);
  },

  getTherapistClientProgress: async (clientId) => {
    const token = localStorage.getItem('token');
    return await api.getTherapistClientProgress(clientId, token);
  },

  // Care Note Management
  createClientCareNote: async (clientId, careNote, token) => {
    if (!clientId) {
      console.error('createClientCareNote called with undefined clientId');
      throw new Error('Client ID is required to create a care note');
    }
    const response = await api.post(`/therapist/client/${clientId}/care-notes`, careNote, token);
    return response.data || response;
  },

  getClientCareNotes: async (clientId, includeInactive = false, token) => {
    if (!clientId) {
      console.error('getClientCareNotes called with undefined clientId');
      return { careNotes: [] };
    }
    const response = await api.get(`/therapist/client/${clientId}/care-notes${includeInactive ? '?includeInactive=true' : ''}`, token);
    return response.data || response;
  },

  updateCareNote: async (noteId, updates, token) => {
    const response = await api.put(`/therapist/care-notes/${noteId}`, updates, token);
    return response.data || response;
  },

  archiveCareNote: async (noteId, reason, token) => {
    const response = await api.post(`/therapist/care-notes/${noteId}/archive`, { reason }, token);
    return response.data || response;
  },

  reactivateCareNote: async (noteId, token) => {
    const response = await api.post(`/therapist/care-notes/${noteId}/reactivate`, {}, token);
    return response.data || response;
  },

  // Alert Management
  getPendingAlerts: async (token) => {
    const response = await api.get('/therapist/alerts/pending', token);
    return response.data || response;
  },

  getClientAlerts: async (clientId, token) => {
    const response = await api.get(`/therapist/client/${clientId}/alerts`, token);
    return response.data || response;
  },

  acknowledgeAlert: async (alertId, token) => {
    const response = await api.post(`/therapist/alerts/${alertId}/acknowledge`, {}, token);
    return response.data || response;
  },

  resolveAlert: async (alertId, resolution, token) => {
    const response = await api.post(`/therapist/alerts/${alertId}/resolve`, { resolution }, token);
    return response.data || response;
  },

  dismissAlert: async (alertId, reason, token) => {
    const response = await api.post(`/therapist/alerts/${alertId}/dismiss`, { reason }, token);
    return response.data || response;
  }
};

export default therapistApi;